import React, { useState } from 'react';

import { DialogContent } from '@mui/material';
import { AlineDialog, AlineDialogButtons, DocumentPicker } from 'src/components';
import { useSelectCurrentUser } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { useAreWorkflowStepsEditable, useGetWorkflowFiles } from 'src/hooks';
import { alineLinkStyles } from 'src/theme';
import { getCanDocsBeAddedToWorkflow } from 'src/workflow';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  buttons: {
    flexShrink: 0,
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '75vh',
    maxHeight: 'calc(100vh - 64px)',
    maxWidth: 1080,
    minWidth: '75%',
  },
  content: {
    boxSizing: 'border-box' as const,
    display: 'flex',
    flex: '1 1 0',
    flexDirection: 'column' as const,
    minHeight: 0,
    width: '100%',
  },
  pauseText: {
    ...alineLinkStyles,
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 600,
    marginLeft: theme.spacing(1),
  },
}));

interface AddWorkflowDocumentDialogProps {
  onClose: () => void;
  open: boolean;
  workflowId: UUID;
  documentVariant?: Apollo.DocumentVariant;
}

export function AddWorkflowDocumentDialog({
  documentVariant = Apollo.DocumentVariant.Document,
  onClose,
  open,
  workflowId,
}: AddWorkflowDocumentDialogProps): JSX.Element | null {
  const { classes } = useStyles();
  const currentUser = useSelectCurrentUser();
  const [addDocumentsToWorkflow, { loading: addingFilesToWorkflow }] =
    Apollo.useAddDocumentsToWorkflowMutation();
  const { areStepsEditable } = useAreWorkflowStepsEditable(workflowId);
  const { workflowFiles, workflowWithFiles } = useGetWorkflowFiles(workflowId);
  const workflowFileIds = workflowFiles.map(workflowFile => workflowFile.document.id);
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<UUID[]>([]);
  const canAddDocuments = getCanDocsBeAddedToWorkflow(workflowWithFiles);

  function handleClose(): void {
    setSelectedDocumentIds([]);
    onClose();
  }

  async function addFilesToWorkflow(): Promise<void> {
    if (selectedDocumentIds.length)
      await addDocumentsToWorkflow({
        variables: { input: { documentIds: selectedDocumentIds, workflowId } },
      });

    handleClose();
  }

  if (!currentUser?.myRepositoryId) return null;

  return (
    <>
      <AlineDialog
        classes={{ paperWidthMd: classes.container }}
        open={open}
        title={
          documentVariant === Apollo.DocumentVariant.Document ? 'Add Documents' : 'Add Templates'
        }
        onClose={handleClose}
      >
        <DialogContent className={classes.content}>
          <DocumentPicker
            documentVariant={
              // Can only add templates to a TWF...
              workflowWithFiles?.status === Apollo.WorkflowStatus.Template
                ? Apollo.DocumentVariant.Template
                : documentVariant
            }
            getIsRowDisabled={document => {
              if (!canAddDocuments)
                return { isRowDisabled: true, reason: 'Cannot add document to workflow' };

              if (!areStepsEditable)
                return {
                  isRowDisabled: true,
                  reason: 'Cannot add documents to a workflow that has been sent for signature',
                };

              const isAlreadyIncluded = workflowFileIds.includes(document.id);
              if (isAlreadyIncluded)
                return {
                  isRowDisabled: true,
                  reason: 'Document already included in the workflow',
                };

              const isPartOfConflictingWorkflow =
                !!document.workflow &&
                document.workflow.variant === workflowWithFiles?.variant &&
                document.workflow.id !== workflowId;
              if (isPartOfConflictingWorkflow)
                return {
                  isRowDisabled: true,
                  reason: 'Document is part of a conflicting workflow',
                };

              return {
                isRowDisabled: false,
                reason: '',
              };
            }}
            repositoryId={currentUser.myRepositoryId}
            onSelectionChange={ids => setSelectedDocumentIds(ids)}
          />
        </DialogContent>
        <AlineDialogButtons
          cancelHandler={handleClose}
          className={classes.buttons}
          disabled={!areStepsEditable}
          loading={addingFilesToWorkflow}
          submitHandler={addFilesToWorkflow}
          submitText={
            selectedDocumentIds.length ? `Add ${selectedDocumentIds.length} files` : 'Done'
          }
        />
      </AlineDialog>
    </>
  );
}
